// MyBookings.jsx
import React, { useState, useEffect } from "react";
import {
  Star,
  ChevronDown,
  ChevronUp,
  X,
  Users,
  Clock,
  Check,
  MessageSquare,
} from "react-feather";
import styles from "./MyBookings.module.css";
import axios from "axios";

const MyBookings = ({ bookings, onSubmitReview, onCancelBooking }) => {
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [bookingInvitations, setBookingInvitations] = useState({});
  const [hoveredStar, setHoveredStar] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(null);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: "",
  });

  console.log("bookings", bookings);

  const toggleBookingDetails = (bookingId) => {
    setExpandedBooking(expandedBooking === bookingId ? null : bookingId);

    // Fetch invitations for this booking if not already fetched
    if (!bookingInvitations[bookingId]) {
      fetchBookingInvitations(bookingId);
    }
  };

  const fetchBookingInvitations = async (bookingId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/booking-invitations?bookingId=${bookingId}`,
        { withCredentials: true }
      );
      setBookingInvitations((prev) => ({
        ...prev,
        [bookingId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching booking invitations:", error);
    }
  };

  const handleStarClick = (bookingId, rating) => {
    setReviewData({ rating, comment: "" });
    setShowReviewForm(bookingId);
  };

  const handleReviewSubmit = async (bookingId) => {
    if (reviewData.rating === 0) {
      alert("Please select a rating");
      return;
    }

    await onSubmitReview(bookingId, reviewData.rating, reviewData.comment);
    setShowReviewForm(null);
    setReviewData({ rating: 0, comment: "" });
  };

  const handleReviewCancel = () => {
    setShowReviewForm(null);
    setReviewData({ rating: 0, comment: "" });
    setHoveredStar(0);
  };

  const isWithinCancellationWindow = (booking) => {
    if (!booking.date || !booking.startTime) {
      console.warn("Booking is missing date or startTime:", booking);
      return false;
    }
    
    const bookingStartDate = new Date(`${booking.date}T${booking.startTime}`);
    const now = new Date();

    // Calculate 12 hours before booking time
    const twelveHoursBefore = new Date(
      bookingStartDate.getTime() - 12 * 60 * 60 * 1000
    );
    // Can cancel if current time is before 12 hours before booking
    return now < twelveHoursBefore;
  };

  const canBeCancelled = (booking) => {
    return booking.status === "Approved" && isWithinCancellationWindow(booking);
  };

  const isDatePassed = (date, time) => {
    try {
      // Create proper date object from the booking date and time
      let bookingDate;

      if (date.includes("T")) {
        // If date already contains time
        bookingDate = new Date(date);
      } else {
        // Combine date and time
        if (time && time.includes("T")) {
          const timeOnly = new Date(time).toTimeString().split(" ")[0];
          bookingDate = new Date(`${date}T${timeOnly}`);
        } else {
          bookingDate = new Date(`${date}T${time || "00:00:00"}`);
        }
      }

      const now = new Date();
      return bookingDate < now;
    } catch (error) {
      console.error("Error parsing date:", error);
      return false;
    }
  };

  const isBookingDateTimePassed = (booking) => {
    try {
      // Use endTime for rating eligibility (user can rate after the booking has ended)
      const bookingEndDate = new Date(`${booking.date}T${booking.endTime}`);
      const now = new Date();
      return bookingEndDate < now;
    } catch (error) {
      console.error("Error checking booking end time:", error);
      return false;
    }
  };

  const getInvitationStatusDisplay = (invitation, bookingDate, bookingTime) => {
    if (
      isDatePassed(bookingDate, bookingTime) &&
      invitation.status === "pending"
    ) {
      return "missed";
    }
    return invitation.status;
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return styles.statusPending;
      case "accepted":
        return styles.statusAccepted;
      case "rejected":
        return styles.statusRejected;
      case "missed":
        return styles.statusMissed;
      default:
        return styles.statusDefault;
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <Clock size={14} />;
      case "accepted":
        return <Check size={14} />;
      case "rejected":
        return <X size={14} />;
      case "missed":
        return <Clock size={14} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.bookingsGrid}>
      {bookings.map((booking) => (
        <div key={booking.id} className={styles.bookingCard}>
          <div
            className={styles.bookingHeader}
            onClick={() => toggleBookingDetails(booking.id)}
          >
            <div className={styles.headerLeft}>
              <h3>{booking.arenaName}</h3>
              <span
                className={`${styles.bookingStatus} ${
                  styles[booking.status.toLowerCase().replace(/\s/g, "")]
                }`}
              >
                {booking.status}
              </span>
            </div>
            <div className={styles.headerRight}>
              {bookingInvitations[booking.id] &&
                bookingInvitations[booking.id].length > 0 && (
                  <div className={styles.invitationsBadge}>
                    <Users size={16} />
                    <span>{bookingInvitations[booking.id].length}</span>
                  </div>
                )}
              {expandedBooking === booking.id ? <ChevronUp /> : <ChevronDown />}
            </div>
          </div>
          {expandedBooking === booking.id && (
            <div className={styles.bookingDetails}>
              <div className={styles.bookingInfo}>
                <p>
                  Date: <span>{booking.date}</span>
                </p>
                <p>
                  Time:{" "}
                  <span>{booking.startTime + " - " + booking.endTime}</span>
                </p>
                <p>
                  Facility: <span>{booking.facility}</span>
                </p>
                <p>
                  Price: <span>${booking.price}</span>
                </p>
              </div>

              {/* Invited Users Section */}
              {bookingInvitations[booking.id] &&
                bookingInvitations[booking.id].length > 0 && (
                  <div className={styles.invitationsSection}>
                    <h4 className={styles.invitationsTitle}>
                      <Users size={16} />
                      Invited Players ({bookingInvitations[booking.id].length})
                    </h4>
                    <div className={styles.invitationsList}>
                      {bookingInvitations[booking.id].map((invitation) => {
                        const displayStatus = getInvitationStatusDisplay(
                          invitation,
                          booking.date,
                          booking.startTime
                        );
                        return (
                          <div
                            key={invitation.id}
                            className={styles.invitationItem}
                          >
                            <div className={styles.inviteeInfo}>
                              <span className={styles.inviteeName}>
                                {invitation.invitee_name}
                              </span>
                              <span className={styles.inviteeEmail}>
                                {invitation.invitee_email}
                              </span>
                            </div>
                            <div
                              className={`${
                                styles.invitationStatus
                              } ${getStatusBadgeClass(displayStatus)}`}
                            >
                              {getStatusIcon(displayStatus)}
                              <span>{displayStatus}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              <div className={styles.bookingActions}>
                {/* Cancel button - show if booking is approved and within cancellation window */}
                {booking.status === "Approved" && canBeCancelled(booking) && (
                  <button
                    className={styles.cancelButton}
                    onClick={() => onCancelBooking(booking)}
                  >
                    <X size={12} /> Cancel Booking
                  </button>
                )}

                {/* Review section - show if booking is approved, no rating given, and booking has ended */}
                {booking.status === "Approved" &&
                  !booking.userRating &&
                  isBookingDateTimePassed(booking) && (
                    <div className={styles.reviewSection}>
                      {showReviewForm === booking.id ? (
                        <div className={styles.reviewForm}>
                          <p>Rate your experience for {booking.facility}</p>
                          <div className={styles.starRating}>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                onClick={() =>
                                  setReviewData((prev) => ({
                                    ...prev,
                                    rating: star,
                                  }))
                                }
                                className={styles.starIcon}
                                fill={
                                  star <= reviewData.rating ? "#ffd700" : "none"
                                }
                                onMouseEnter={() => setHoveredStar(star)}
                                onMouseLeave={() => setHoveredStar(0)}
                              />
                            ))}
                          </div>
                          <div className={styles.commentSection}>
                            <div className={styles.commentInputContainer}>
                              <MessageSquare
                                size={16}
                                className={styles.commentIcon}
                              />
                              <textarea
                                value={reviewData.comment}
                                onChange={(e) =>
                                  setReviewData((prev) => ({
                                    ...prev,
                                    comment: e.target.value,
                                  }))
                                }
                                placeholder="Add a comment about your experience (optional)"
                                className={styles.commentInput}
                                rows={3}
                                maxLength={500}
                              />
                            </div>
                            <span className={styles.characterCount}>
                              {reviewData.comment.length}/500
                            </span>
                          </div>
                          <div className={styles.reviewButtons}>
                            <div>
                              <button
                                onClick={() => handleReviewSubmit(booking.id)}
                                className={styles.submitReviewButton}
                                disabled={reviewData.rating === 0}
                              >
                                Submit Review
                              </button>
                              <button
                                onClick={handleReviewCancel}
                                className={styles.cancelReviewButton}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.ratePrompt}>
                          <p>Rate your experience for {booking.facility}</p>
                          <div className={styles.starRating}>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                onClick={() =>
                                  handleStarClick(booking.id, star)
                                }
                                className={styles.starIcon}
                                fill={star <= hoveredStar ? "#ffd700" : "none"}
                                onMouseEnter={() => setHoveredStar(star)}
                                onMouseLeave={() => setHoveredStar(0)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                {booking.userRating && (
                  <div className={styles.userReview}>
                    <p className={styles.userRating}>
                      Your rating for {booking.facility}: {booking.userRating}{" "}
                      stars
                    </p>
                    {booking.userComment && (
                      <p className={styles.userComment}>
                        Your comment: "{booking.userComment}"
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default MyBookings;
